// apps/admin-ui/src/hooks/useExchangeAdmin.ts
// Exchange Admin Hooks with proper authentication
// ALL RIGHTS RESERVED
//
// FIXED 2026-02-02: Changed port from 3060 to 3009 (actual exchange service port)
// ADDED 2026-02-09: useDexOnRampOrders hook (was incorrectly routed through ledger client)

import { useState, useCallback, useEffect } from 'react';
import { 
  getStoredEmployeeId, 
  getStoredFingerprint, 
  getCurrentEmployee,
  isDevMode,
} from '../utils/auth';
import { API_CONFIG } from '../config/api';

// =============================================================================
// TYPES
// =============================================================================

export interface ExchangeStats {
  volume: string;
  orderCount: number;
  buyCount: number;
  buyVolume: string;
  sellCount: number;
  sellVolume: string;
  pendingCount: number;
  failedCount: number;
  avgOrderSize: string;
  totalFees: string;
  period: string;
}

export interface AdminOrder {
  id: string;
  userId: string;
  side: 'buy' | 'sell';
  status: 'pending' | 'submitted' | 'filled' | 'settled' | 'failed' | 'cancelled';
  amountUsd: string;
  amountCxxr: string;
  feeAmount: string;
  feePercent: string;
  netAmount: string;
  coinbaseOrderId: string | null;
  coinbaseStatus: string | null;
  filledPrice: string | null;
  filledAt: string | null;
  settledAt: string | null;
  ledgerTxnId: string | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

interface OrderFilters {
  status?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  side?: string;
  minAmount?: string;
  maxAmount?: string;
  limit?: number;
  offset?: number;
}

export interface DexOnRampOrder {
  id: string;
  quoteId: string;
  userId: string;
  userEmail?: string;
  status: string;
  fromToken: string;
  fromAmount: string;
  cxxrCredited: string;
  walletAddress: string;
  txHash?: string;
  networkMode: 'mainnet' | 'testnet';
  createdAt: string;
  confirmedAt?: string;
}

interface DexOnRampFilters {
  status?: string;
  networkMode?: string;
  page: number;
  pageSize: number;
}

// =============================================================================
// API CLIENT WITH AUTH
// =============================================================================

// FIXED: Port 3009 is the actual exchange service port (was 3060)
const EXCHANGE_URL = API_CONFIG.exchange;

async function adminRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  // Add auth headers
  const fingerprint = getStoredFingerprint();
  const employeeId = getStoredEmployeeId();

  if (fingerprint) {
    // Real auth: use certificate fingerprint
    headers['x-client-cert-fingerprint'] = fingerprint;
  } else if (employeeId && isDevMode()) {
    // Dev mode: use dev employee ID and roles
    headers['x-dev-employee-id'] = employeeId;
    const employee = getCurrentEmployee();
    if (employee) {
      // Send primary role for backward compat
      headers['x-dev-employee-role'] = employee.roles[0] || 'admin';
      // Also send all roles
      headers['x-dev-employee-roles'] = JSON.stringify(employee.roles);
    }
  }

  const response = await fetch(`${EXCHANGE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }

  return response.json();
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Get exchange statistics
 */
export function useExchangeStats(period: '24h' | '7d' | '30d' = '24h') {
  const [stats, setStats] = useState<ExchangeStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await adminRequest<ExchangeStats>(`/admin/stats?period=${period}`);
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch stats'));
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  // Initial fetch
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, isLoading, error, refetch: fetchStats };
}

/**
 * Get and manage admin orders
 */
export function useAdminOrders(filters: OrderFilters = {}) {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.userId) params.set('userId', filters.userId);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      if (filters.side) params.set('side', filters.side);
      if (filters.minAmount) params.set('minAmount', filters.minAmount);
      if (filters.maxAmount) params.set('maxAmount', filters.maxAmount);
      if (filters.limit) params.set('limit', filters.limit.toString());
      if (filters.offset) params.set('offset', filters.offset.toString());

      const query = params.toString();
      const data = await adminRequest<{ orders: AdminOrder[]; pagination: { total: number } }>(
        `/admin/orders${query ? '?' + query : ''}`
      );
      setOrders(data.orders);
      setTotal(data.pagination.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch orders'));
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.status, 
    filters.userId, 
    filters.startDate, 
    filters.endDate, 
    filters.side, 
    filters.minAmount, 
    filters.maxAmount, 
    filters.limit, 
    filters.offset
  ]);

  // Initial fetch
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const cancelOrder = useCallback(async (orderId: string, reason: string) => {
    const result = await adminRequest<{ order: AdminOrder }>(
      `/admin/orders/${orderId}/cancel`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }
    );
    return result.order;
  }, []);

  const retrySettlement = useCallback(async (orderId: string) => {
    const result = await adminRequest<{ order: AdminOrder }>(
      `/admin/orders/${orderId}/retry-settlement`,
      { method: 'POST' }
    );
    return result.order;
  }, []);

  return {
    orders,
    total,
    isLoading,
    error,
    refetch: fetchOrders,
    cancelOrder,
    retrySettlement,
  };
}

/**
 * Get hourly volume data for charts
 */
export function useHourlyVolume(hours: number = 24) {
  const [data, setData] = useState<Array<{ hour: string; volume: string; count: number }>>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await adminRequest<{ hourlyVolume: typeof data }>(
        `/admin/stats/hourly?hours=${hours}`
      );
      setData(result.hourlyVolume);
    } catch (err) {
      console.error('Failed to fetch hourly volume:', err);
    } finally {
      setIsLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, refetch: fetchData };
}

/**
 * Get DEX OnRamp statistics
 */
export function useDexOnRampStats() {
  const [stats, setStats] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await adminRequest<any>('/api/v1/admin/dex/onramp/stats');
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch DEX stats'));
      // Return mock data for development
      setStats({
        pending: 0,
        submitted: 0,
        completed: 0,
        failed: 0,
        total: 0,
        totalVolume: 0,
        recentOrders: [],
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, isLoading, error, refetch: fetchStats };
}

/**
 * Get DEX OnRamp orders with pagination and filters.
 * Routes through the exchange service (API_CONFIG.exchange) — NOT the ledger client.
 */
export function useDexOnRampOrders(filters: DexOnRampFilters) {
  const [orders, setOrders] = useState<DexOnRampOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(filters.page),
        pageSize: String(filters.pageSize),
      });
      if (filters.status) params.set('status', filters.status);
      if (filters.networkMode) params.set('networkMode', filters.networkMode);

      const data = await adminRequest<{ orders: DexOnRampOrder[]; total: number }>(
        `/api/v1/admin/dex/onramp/orders?${params}`
      );
      setOrders(data.orders || []);
      const t = data.total || 0;
      setTotal(t);
      setTotalPages(Math.ceil(t / filters.pageSize));
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load orders';
      console.error('Failed to fetch DEX onramp orders:', err);
      setError(message);
      // Mock data for development
      setOrders([
        {
          id: '3838978000001',
          quoteId: 'q-001',
          userId: '1',
          userEmail: 'alice1@test.com',
          status: 'completed',
          fromToken: 'WETH',
          fromAmount: '0.1',
          cxxrCredited: '216.21',
          walletAddress: '0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266',
          txHash: '0x0000000000000000000000000000000000000000000000000003838978000001',
          networkMode: 'testnet',
          createdAt: '2026-02-02T03:45:00Z',
          confirmedAt: '2026-02-02T03:45:05Z',
        },
        {
          id: '3838978000002',
          quoteId: 'q-002',
          userId: '2',
          userEmail: 'bob@test.com',
          status: 'pending',
          fromToken: 'ETH',
          fromAmount: '0.5',
          cxxrCredited: '1081.05',
          walletAddress: '0x70997970C51812dc3A010C7d01b50e0d17dc79C8',
          networkMode: 'testnet',
          createdAt: '2026-02-02T04:00:00Z',
        },
      ]);
      setTotal(2);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [filters.page, filters.pageSize, filters.status, filters.networkMode]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return { orders, total, totalPages, isLoading, error, refetch: fetchOrders };
}

export default {
  useExchangeStats,
  useAdminOrders,
  useHourlyVolume,
  useDexOnRampStats,
  useDexOnRampOrders,
};
